import { useCallback, useEffect, useMemo, useState } from 'react';
import { VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { useOrganization } from '../../../contexts/OrganizationContext';
import type { SettingsFieldType } from '../components/EditFieldModal';
import { EditFieldModal } from '../components/EditFieldModal';
import { EntityTabs } from '../components/EntityTabs';
import { SettingsRow } from '../components/SettingsRow';
import { useSettingsMutations } from '../useSettingsMutations';
import { ORGANIZATION_TAB_ID } from '../utils/constants';
import { getEditableSettingsEntities } from '../utils/getEditableSettingsEntities';

const messages = defineMessages({
  title: {
    id: 'Settings.home.title',
    defaultMessage: 'Settings',
    description: 'Title for the workspace Settings page',
  },
  nameLabel: {
    id: 'Settings.home.nameLabel',
    defaultMessage: 'Display Name',
    description: 'Label for the organization or legal entity name setting',
  },
  saveSuccessOrganization: {
    id: 'Settings.home.saveSuccessOrganization',
    defaultMessage: 'Organization name updated',
    description: 'Toast shown after successfully updating an organization name',
  },
  saveSuccessLegalEntity: {
    id: 'Settings.home.saveSuccessLegalEntity',
    defaultMessage: 'Legal entity name updated',
    description: 'Toast shown after successfully updating a legal entity name',
  },
  saveError: {
    id: 'Settings.home.saveError',
    defaultMessage: 'Error updating Display Name',
    description: 'Toast shown when updating an organization or legal entity name fails',
  },
});

export function SettingsPage() {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { selectedOrganization } = useOrganization();

  // admin-entities returns every visible LE, but Settings should only surface the
  // ones the user can administer (org admin, or LE admin on that specific entity).
  const editableEntities = useMemo(
    () => getEditableSettingsEntities(selectedOrganization),
    [selectedOrganization],
  );

  const [activeTabId, setActiveTabId] = useState(
    () => editableEntities[0]?.id ?? ORGANIZATION_TAB_ID,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { updateName } = useSettingsMutations(selectedOrganization?.organizationId ?? '');

  useEffect(() => {
    logEvent('settings_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  const activeEntity = useMemo(
    () => editableEntities.find((entity) => entity.id === activeTabId),
    [activeTabId, editableEntities],
  );

  useEffect(() => {
    if (activeEntity) {
      return;
    }

    setActiveTabId(editableEntities[0]?.id ?? ORGANIZATION_TAB_ID);
    setIsEditing(false);
  }, [activeEntity, editableEntities]);

  const canEditOrganization = useMemo(
    () => editableEntities.some((entity) => entity.id === ORGANIZATION_TAB_ID),
    [editableEntities],
  );
  const legalEntities = useMemo(
    () =>
      editableEntities
        .filter((entity) => entity.id !== ORGANIZATION_TAB_ID)
        .map(({ id, name }) => ({ legalEntityId: id, legalEntityName: name })),
    [editableEntities],
  );

  const nameLabel = useMemo(() => formatMessage(messages.nameLabel), [formatMessage]);
  const name = activeEntity?.name ?? '';
  const canEdit = activeEntity != null;
  const fieldType = useMemo<SettingsFieldType>(
    () => (activeTabId === ORGANIZATION_TAB_ID ? 'org_name' : 'legal_entity_name'),
    [activeTabId],
  );

  const handleOpenEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSaveName = useCallback(
    async (updatedName: string) => {
      setIsSaving(true);

      try {
        await updateName({ entityId: activeTabId, name: updatedName });
        const successMessage =
          activeTabId === ORGANIZATION_TAB_ID
            ? messages.saveSuccessOrganization
            : messages.saveSuccessLegalEntity;
        toast.show(formatMessage(successMessage));
        setIsEditing(false);
      } catch {
        toast.show(formatMessage(messages.saveError), { variant: 'bgNegative' });
      } finally {
        setIsSaving(false);
      }
    },
    [activeTabId, formatMessage, toast, updateName],
  );

  return (
    <VStack className="workspaceContentFadeIn" gap={3} width="100%" data-testid="settings-page">
      <Text as="h1" font="title1">
        {formatMessage(messages.title)}
      </Text>
      {editableEntities.length > 0 && (
        <EntityTabs
          legalEntities={legalEntities}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
          showOrganization={canEditOrganization}
        />
      )}
      {canEdit && (
        <>
          <SettingsRow
            icon="star"
            title={nameLabel}
            description={name}
            onClick={handleOpenEdit}
            testID="settings-display-name-row"
          />
          <EditFieldModal
            visible={isEditing}
            title={nameLabel}
            fieldType={fieldType}
            initialValue={name}
            onSave={handleSaveName}
            onClose={handleCloseEdit}
            isSaving={isSaving}
          />
        </>
      )}
    </VStack>
  );
}
